import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {
  Template,
  Templates,
  SchedulerTemplatesStateQueryParams,
} from 'src/types/scheduler/templates';

import {camel, snake} from 'src/utils';
import * as reduxApi from 'src/utils/redux-api-v2';

import {
  selectTeamTemplatesQueryParams,
  selectIndividualTemplatesQueryParams,
} from 'src/selectors/scheduler/templates';
import {selectAgentApiParams} from 'src/selectors/scheduler/global';


export type SchedulerTemplatesActions =
  | SetIndividualTemplatesAction
  | AppendIndividualTemplatesAction
  | SetIndividualTemplatesQueryParamsAction
  | AddNewIndividualTemplateAction
  | SetTeamTemplatesAction
  | AppendTeamTemplatesAction
  | SetTeamTemplatesQueryParamsAction
  | AddNewTeamTemplateAction
  | RemoveTemplateAction
  | CopyTemplateAction;

// individual templates constants
export const SET_INDIVIDUAL_TEMPLATES =
  'scheduling/templates/setIndividualTemplates';
export const SET_INDIVIDUAL_TEMPLATES_QUERY_PARAMS =
  'scheduling/templates/setIndividualTemplatesQueryParams';
export const ADD_NEW_INDIVIDUAL_TEMPLATE =
  'scheduling/templates/addNewIndividualTemplate';
export const APPEND_INDIVIDUAL_TEMPLATES =
  'scheduling/templates/appendIndividualTemplates';

// team templates constants
export const SET_TEAM_TEMPLATES = 'scheduling/templates/setTeamTemplates';
export const SET_TEAM_TEMPLATES_QUERY_PARAMS =
  'scheduling/templates/setTeamTemplatesQueryParams';
export const ADD_NEW_TEAM_TEMPLATE = 'scheduling/templates/addNewTeamTemplate';
export const APPEND_TEAM_TEMPLATES = 'scheduling/templates/appendTeamTemplates';

// extra templates constants
export const REMOVE_TEMPLATE = 'scheduling/templates/removeTemplate';
export const COPY_TEMPLATE = 'scheduling/templates/copyTemplate';

type templatesPayload = {
  data: Templates;
  totalCount: number;
};

// set individual templates
export type SetIndividualTemplatesAction = {
  type: typeof SET_INDIVIDUAL_TEMPLATES;
  payload: templatesPayload;
};
export const setIndividualTemplates = (
  payload: templatesPayload,
): SetIndividualTemplatesAction => ({
  type: SET_INDIVIDUAL_TEMPLATES,
  payload,
});

// append individual templates
export type AppendIndividualTemplatesAction = {
  type: typeof APPEND_INDIVIDUAL_TEMPLATES;
  payload: templatesPayload;
};
export const appendIndividualTemplates = (
  payload: templatesPayload,
): AppendIndividualTemplatesAction => ({
  type: APPEND_INDIVIDUAL_TEMPLATES,
  payload,
});

// set individual templates query params
export type SetIndividualTemplatesQueryParamsAction = {
  type: typeof SET_INDIVIDUAL_TEMPLATES_QUERY_PARAMS;
  payload: SchedulerTemplatesStateQueryParams;
};
export const setIndividualTemplatesQueryParams = (
  payload: SchedulerTemplatesStateQueryParams,
): SetIndividualTemplatesQueryParamsAction => ({
  type: SET_INDIVIDUAL_TEMPLATES_QUERY_PARAMS,
  payload,
});

// add new individual template
export type AddNewIndividualTemplateAction = {
  type: typeof ADD_NEW_INDIVIDUAL_TEMPLATE;
  payload: Template;
};
export const addNewIndividualTemplate = (
  payload: Template,
): AddNewIndividualTemplateAction => ({
  type: ADD_NEW_INDIVIDUAL_TEMPLATE,
  payload,
});

// set team templates
export type SetTeamTemplatesAction = {
  type: typeof SET_TEAM_TEMPLATES;
  payload: templatesPayload;
};
export const setTeamTemplates = (
  payload: templatesPayload,
): SetTeamTemplatesAction => ({
  type: SET_TEAM_TEMPLATES,
  payload,
});

// append team templates
export type AppendTeamTemplatesAction = {
  type: typeof APPEND_TEAM_TEMPLATES;
  payload: templatesPayload;
};
export const appendTeamTemplates = (
  payload: templatesPayload,
): AppendTeamTemplatesAction => ({
  type: APPEND_TEAM_TEMPLATES,
  payload,
});

// set team templates query params
export type SetTeamTemplatesQueryParamsAction = {
  type: typeof SET_TEAM_TEMPLATES_QUERY_PARAMS;
  payload: SchedulerTemplatesStateQueryParams;
};
export const setTeamTemplatesQueryParams = (
  payload: SchedulerTemplatesStateQueryParams,
): SetTeamTemplatesQueryParamsAction => ({
  type: SET_TEAM_TEMPLATES_QUERY_PARAMS,
  payload,
});

// add new individual template
export type AddNewTeamTemplateAction = {
  type: typeof ADD_NEW_TEAM_TEMPLATE;
  payload: Template;
};
export const addNewTeamTemplate = (
  payload: Template,
): AddNewTeamTemplateAction => ({
  type: ADD_NEW_TEAM_TEMPLATE,
  payload,
});

// copy template
type CopyTemplatePayload = {
  template: Template;
  isTeamTemplate: boolean;
};
export type CopyTemplateAction = {
  type: typeof COPY_TEMPLATE;
  payload: CopyTemplatePayload;
};
export const copyTemplate = (
  payload: CopyTemplatePayload,
): CopyTemplateAction => ({
  type: COPY_TEMPLATE,
  payload,
});

// remove template
type RemoveTemplatePayload = {
  templateId: string;
  isTeamTemplate: boolean;
};
export type RemoveTemplateAction = {
  type: typeof REMOVE_TEMPLATE;
  payload: RemoveTemplatePayload;
};
export const removeTemplate = (
  payload: RemoveTemplatePayload,
): RemoveTemplateAction => ({
  type: REMOVE_TEMPLATE,
  payload,
});

export const fetchIndividualTemplates =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const individualTemplatesQueryParams =
      selectIndividualTemplatesQueryParams(state);
    const agentApiParams = selectAgentApiParams(state);

    const payload = {
      ...agentApiParams,
      ...individualTemplatesQueryParams,
    } as const;
    try {
      const result = await dispatch(
        reduxApi.post('scheduler/event-templates/list', snake(payload)),
      );

      const {data, total_count} = result || {};
      const individualTemplatesPayload = {
        data: camel(data, true, ['blocks', 'entityMap']),
        totalCount: total_count,
      } as const;

      if (individualTemplatesQueryParams.offset === '0') {
        dispatch(setIndividualTemplates(individualTemplatesPayload));
      } else {
        dispatch(appendIndividualTemplates(individualTemplatesPayload));
      }
    } catch (err: any) {
      throw err;
    }
  };

export const createIndividualTemplate =
  (distributionRule: string): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const timezone: string = getTimezone();
    const actorApiParams = selectAgentApiParams(state);

    try {
      const result = await dispatch(
        reduxApi.post(
          'scheduler/event-templates',
          snake({
            timezone,
            distributionRule,
            ...actorApiParams,
          }),
        ),
      );
      const newIndividualTemplate = camel(result, true, [
        'blocks',
        'entityMap',
      ]);
      dispatch(addNewIndividualTemplate(newIndividualTemplate));
      return newIndividualTemplate;
    } catch (err: any) {
      throw err;
    }
  };

export const fetchTeamTemplates =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const teamTemplatesQueryParams = selectTeamTemplatesQueryParams(state);
    const agentApiParams = selectAgentApiParams(state);

    const payload = {
      ...agentApiParams,
      ...teamTemplatesQueryParams,
    } as const;

    try {
      const result = await dispatch(
        reduxApi.post('scheduler/event-templates/team/list', snake(payload)),
      );

      const {data, total_count} = result || {};
      const teamTemplatesPayload = {
        data: camel(data, true, ['blocks', 'entityMap']),
        totalCount: total_count,
      } as const;

      if (teamTemplatesQueryParams.offset === '0') {
        dispatch(setTeamTemplates(teamTemplatesPayload));
      } else {
        dispatch(appendTeamTemplates(teamTemplatesPayload));
      }
    } catch (err: any) {
      throw err;
    }
  };

export const createTeamTemplate =
  (distributionRule: string): ThunkAction =>
  async (dispatch: Dispatch) => {
    const timezone: string = getTimezone();

    try {
      const result = await dispatch(
        reduxApi.post(
          'scheduler/event-templates/team',
          snake({
            timezone,
            distributionRule,
          }),
        ),
      );
      const newTeamTemplate = camel(result, true, ['blocks', 'entityMap']);
      dispatch(addNewTeamTemplate(newTeamTemplate));
      return newTeamTemplate;
    } catch (err: any) {
      throw err;
    }
  };

export const deleteTemplate =
  ({templateId, isTeamTemplate}: RemoveTemplatePayload): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(reduxApi.del(`scheduler/event-template/${templateId}`));
      dispatch(removeTemplate({templateId, isTeamTemplate}));
    } catch (err: any) {
      throw err;
    }
  };

export const copyAndCreateTemplate =
  ({
    templateId,
    isTeamTemplate,
  }: {
    templateId: string;
    isTeamTemplate: boolean;
  }): ThunkAction =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const actorApiParams = selectAgentApiParams(state);
    try {
      const result = await dispatch(
        reduxApi.post(
          `scheduler/event-template/${templateId}/copy`,
          snake(actorApiParams),
        ),
      );
      const copiedTemplate = camel(result, true, ['blocks', 'entityMap']);
      dispatch(copyTemplate({template: copiedTemplate, isTeamTemplate}));
      return copiedTemplate;
    } catch (err: any) {
      throw err;
    }
  };

//utils functions
const getTimezone = (): string => {
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  return timezone;
};
